// Vector Drift's online craft states (ACT HQ t-492, act-babylon DR-0094). A page sends its
// own craft about 20 times a second (and the bot host its bots in the same message); the
// room checks them and passes them on to everyone else. One flat array of integers per
// message: msgpack keeps small integers small, so a craft is about 30 bytes.
//
// Per craft, VD_STATE_STRIDE numbers: slot, x, y, z (hundredths of a unit), the
// orientation quaternion qx, qy, qz, qw (ten-thousandths), speed (tenths of a unit a
// second), progress round the lap (ten-thousandths of the lap) and flags (VD_STATE_FLAG).

import { VD_ONLINE_SEATS } from "../types/vectorDrift";

export const VD_STATE_STRIDE = 11;

export const VD_STATE_FLAG = {
  airborne: 1,
  boosting: 2,
  /** The craft was just put back on the road after a fall: it may jump a long way. */
  respawned: 4,
  drifting: 8,
} as const;

export type VdCraftState = {
  slot: number;
  x: number;
  y: number;
  z: number;
  qx: number;
  qy: number;
  qz: number;
  qw: number;
  /** Units a second. */
  speed: number;
  /** Progress round the lap, 0..1. */
  trackT: number;
  flags: number;
};

/** The farthest a craft can be from the course's origin (units): courses span a few thousand. */
const MAX_COORD = 100_000;
/** The fastest any craft can go (units a second; a boost pad gives about 364). */
const MAX_SPEED = 1_000;

const POS = 100;
const ROT = 10_000;
const SPEED = 10;
const T = 10_000;

/** Pack crafts for a message. Out-of-range values are clamped, so packing never fails. */
export function packCraftStates(states: readonly VdCraftState[]): number[] {
  const out: number[] = [];
  const clamp = (v: number, lim: number) => Math.max(-lim, Math.min(lim, Number.isFinite(v) ? v : 0));
  for (const s of states) {
    out.push(
      s.slot,
      Math.round(clamp(s.x, MAX_COORD) * POS),
      Math.round(clamp(s.y, MAX_COORD) * POS),
      Math.round(clamp(s.z, MAX_COORD) * POS),
      Math.round(clamp(s.qx, 1) * ROT),
      Math.round(clamp(s.qy, 1) * ROT),
      Math.round(clamp(s.qz, 1) * ROT),
      Math.round(clamp(s.qw, 1) * ROT),
      Math.round(Math.max(0, clamp(s.speed, MAX_SPEED)) * SPEED),
      Math.round(Math.max(0, Math.min(1, Number.isFinite(s.trackT) ? s.trackT : 0)) * T),
      s.flags & 15,
    );
  }
  return out;
}

const inRange = (v: unknown, lo: number, hi: number): v is number => typeof v === "number" && Number.isInteger(v) && v >= lo && v <= hi;

/**
 * Unpack a message's crafts, or null when anything about it is off (not an array of whole
 * numbers, a length that isn't a whole number of crafts, more crafts than seats, a value out
 * of range, the same slot twice).
 */
export function unpackCraftStates(raw: unknown, maxCrafts: number = VD_ONLINE_SEATS): VdCraftState[] | null {
  if (!Array.isArray(raw) || raw.length === 0 || raw.length % VD_STATE_STRIDE !== 0) return null;
  const count = raw.length / VD_STATE_STRIDE;
  if (count > maxCrafts) return null;
  const states: VdCraftState[] = [];
  const seen = new Set<number>();
  for (let i = 0; i < count; i++) {
    const v = raw.slice(i * VD_STATE_STRIDE, (i + 1) * VD_STATE_STRIDE) as unknown[];
    const [slot, x, y, z, qx, qy, qz, qw, speed, t, flags] = v;
    if (!inRange(slot, 0, VD_ONLINE_SEATS - 1) || seen.has(slot)) return null;
    if (!inRange(x, -MAX_COORD * POS, MAX_COORD * POS) || !inRange(y, -MAX_COORD * POS, MAX_COORD * POS) || !inRange(z, -MAX_COORD * POS, MAX_COORD * POS)) return null;
    if (![qx, qy, qz, qw].every((q) => inRange(q, -ROT, ROT))) return null;
    if (!inRange(speed, 0, MAX_SPEED * SPEED) || !inRange(t, 0, T) || !inRange(flags, 0, 15)) return null;
    seen.add(slot);
    states.push({
      slot,
      x: x / POS,
      y: y / POS,
      z: z / POS,
      qx: (qx as number) / ROT,
      qy: (qy as number) / ROT,
      qz: (qz as number) / ROT,
      qw: (qw as number) / ROT,
      speed: speed / SPEED,
      trackT: t / T,
      flags,
    });
  }
  return states;
}
